import type { NeighbourhoodSpec } from './locationPageConfig.types.js';

/**
 * Major hyper-local landings within ~50 km of Kochi that are not in the
 * long-form or first neighbourhood set. Copy is unique per locality.
 * Super Admin can override title/description via landing-page SEO.
 */
export const KOCHI_RADIUS_50KM_SPECS: NeighbourhoodSpec[] = [
  {
    key: 'thoppumpady',
    name: 'Thoppumpady',
    slug: 'cleaning-service-thoppumpady',
    eyebrow: 'Thoppumpady Bridge, West Kochi & Aroor Road',
    blurb:
      'Home and shop cleaning in Thoppumpady, the West Kochi gateway. Deep cleaning for bridge-side apartments, church homes and small offices toward Fort Kochi and Aroor.',
    highlightIcon: 'Anchor',
    highlightText: 'West Kochi gateway',
    areas: [
      'Thoppumpady Bridge',
      'Thoppumpady Market',
      'Aroor Road',
      'Palluruthy edge',
      'Willingdon approach',
    ],
    landmarks: ['Thoppumpady Bridge', 'Thoppumpady Market', 'Aroor Road'],
    nearby: ['Fort Kochi', 'Palluruthy', 'Willingdon Island'],
    related: [
      { name: 'Fort Kochi', href: '/cleaning-service-fort-kochi' },
      { name: 'Palluruthy', href: '/cleaning-service-palluruthy' },
      { name: 'Willingdon Island', href: '/cleaning-service-willingdon-island' },
    ],
    reviews: [
      {
        name: 'Shaji P.',
        location: 'Thoppumpady',
        text: 'Independent house near the bridge. Salt air on windows needed a real wash, not a wipe.',
      },
      {
        name: 'Liza M.',
        location: 'Market Road',
        text: 'Shop + upstairs home in one booking. Crew finished before evening mass.',
      },
      {
        name: 'Anto K.',
        location: 'Thoppumpady',
        text: 'Bathroom grout treatment before monsoon. Tiles look new.',
      },
    ],
  },
  {
    key: 'mattancherry',
    name: 'Mattancherry',
    slug: 'cleaning-service-mattancherry',
    eyebrow: 'Jew Town, Palace Road & Heritage Homes',
    blurb:
      'Heritage-home and homestay cleaning in Mattancherry. Dust from tourist streets, wooden interiors and courtyard houses need a careful crew — not a hotel-only checklist.',
    highlightIcon: 'Sun',
    highlightText: 'Jew Town & Palace',
    areas: ['Jew Town', 'Mattancherry Palace Road', 'Bazaar Road', 'Fort Kochi edge', 'Koovapadam'],
    landmarks: ['Mattancherry Palace', 'Jew Town', 'Paradesi Synagogue'],
    nearby: ['Fort Kochi', 'Thoppumpady', 'Palluruthy'],
    related: [
      { name: 'Fort Kochi', href: '/cleaning-service-fort-kochi' },
      { name: 'Thoppumpady', href: '/cleaning-service-thoppumpady' },
      { name: 'Palluruthy', href: '/cleaning-service-palluruthy' },
    ],
    reviews: [
      {
        name: 'Rachel D.',
        location: 'Jew Town',
        text: 'Homestay turnaround between guests. Floors and bathrooms ready the same afternoon.',
      },
      {
        name: 'Hassan A.',
        location: 'Bazaar Road',
        text: 'Shop-house deep clean. Respectful around antiques and woodwork.',
      },
      {
        name: 'Maya S.',
        location: 'Palace Road',
        text: 'Courtyard moss and bathroom fungus — both handled.',
      },
    ],
  },
  {
    key: 'vypin',
    name: 'Vypin',
    slug: 'cleaning-service-vypin',
    eyebrow: 'Goshree, Vypeen Island & Cherai approach',
    blurb:
      'Island-home cleaning on Vypin (Vypeen). Salt air, monsoon mould and weekend homestays from Goshree to Njarakkal — deep cleaning that travels across the bridges.',
    highlightIcon: 'Waves',
    highlightText: 'Vypeen island homes',
    areas: ['Goshree', 'Njarakkal', 'Nayarambalam', 'Edavanakkad edge', 'Vypin Ferry'],
    landmarks: ['Goshree Bridges', 'Vypin Lighthouse approach', 'Njarakkal'],
    nearby: ['Cherai', 'Fort Kochi', 'North Paravur'],
    related: [
      { name: 'Cherai', href: '/cleaning-service-cherai' },
      { name: 'Fort Kochi', href: '/cleaning-service-fort-kochi' },
      { name: 'North Paravur', href: '/cleaning-service-north-paravur' },
    ],
    reviews: [
      {
        name: 'Jinson T.',
        location: 'Njarakkal',
        text: 'Villa after Onam guests. Kitchen and outdoor sit-out both cleaned.',
      },
      { name: 'Asha P.', location: 'Goshree', text: 'Apartment near the bridges. Window salt film is gone.' },
      { name: 'Riyas K.', location: 'Vypin', text: 'Booked two houses the same week. Both slots held.' },
    ],
  },
  {
    key: 'ponnurunni',
    name: 'Ponnurunni',
    slug: 'cleaning-service-ponnurunni',
    eyebrow: 'Ponnurunni, Petta & Vyttila west',
    blurb:
      'Apartment and family-house cleaning in Ponnurunni. Convenient for Petta, Vyttila Mobility Hub commuters and SA Road offices that need weekday afternoon slots.',
    highlightIcon: 'Bus',
    highlightText: 'Petta & Vyttila west',
    areas: ['Ponnurunni Junction', 'Petta', 'Vyttila west', 'Chambakkara edge', 'SA Road approach'],
    landmarks: ['Ponnurunni Junction', 'Petta', 'Vyttila Mobility Hub approach'],
    nearby: ['Vyttila', 'Elamkulam', 'Kadavanthra'],
    related: [
      { name: 'Vyttila', href: '/cleaning-service-vyttila' },
      { name: 'Elamkulam', href: '/cleaning-service-elamkulam' },
      { name: 'Kadavanthra', href: '/cleaning-service-kadavanthra' },
    ],
    reviews: [
      {
        name: 'Nithin R.',
        location: 'Ponnurunni',
        text: '2BHK deep clean while I was at the hub. Photos on WhatsApp.',
      },
      {
        name: 'Keerthi L.',
        location: 'Petta',
        text: 'Kitchen chimney was the main job. Grease actually lifted.',
      },
      {
        name: 'Babu M.',
        location: 'Ponnurunni',
        text: 'Fortnightly plan. Bathrooms stay usable in monsoon.',
      },
    ],
  },
  {
    key: 'kundanoor',
    name: 'Kundanoor',
    slug: 'cleaning-service-kundanoor',
    eyebrow: 'Kundanoor Junction, NH Bypass & Nettoor high-rises',
    blurb:
      'High-rise and villa cleaning at Kundanoor Junction. Lake dust, balcony grime and new-tower move-ins on the NH Bypass — a crew that already works this interchange every week.',
    highlightIcon: 'Building',
    highlightText: 'Bypass high-rises',
    areas: [
      'Kundanoor Junction',
      'NH Bypass Kundanoor',
      'Nettoor edge',
      'Maradu lakeside',
      'Thevara approach',
    ],
    landmarks: ['Kundanoor Junction', 'NH Bypass', 'Nettoor lake'],
    nearby: ['Nettoor', 'Maradu', 'Thevara'],
    related: [
      { name: 'Nettoor', href: '/cleaning-service-nettoor' },
      { name: 'Maradu', href: '/cleaning-service-maradu' },
      { name: 'Thevara', href: '/cleaning-service-thevara' },
    ],
    reviews: [
      {
        name: 'Arun V.',
        location: 'Kundanoor',
        text: '3BHK high-rise. Window tracks and balcony rails were the worst — now clean.',
      },
      {
        name: 'Sneha P.',
        location: 'Bypass',
        text: 'Move-in clean after handover. Cement haze on tiles is gone.',
      },
      {
        name: 'Jayan T.',
        location: 'Kundanoor',
        text: 'Same-day kitchen + bathroom before guests from Bangalore.',
      },
    ],
  },
  {
    key: 'pachalam',
    name: 'Pachalam',
    slug: 'cleaning-service-pachalam',
    eyebrow: 'Pachalam, Chittoor Road & North Town',
    blurb:
      'Family-house and apartment cleaning in Pachalam. North Ernakulam streets toward Chittoor Road, Vaduthala and the railway — weekday crews that know the one-ways.',
    highlightIcon: 'MapPin',
    highlightText: 'North town streets',
    areas: ['Pachalam Junction', 'Chittoor Road', 'Vaduthala edge', 'North Railway approach', 'Lissie side'],
    landmarks: ['Pachalam Junction', 'Chittoor Road', 'Lissie approach'],
    nearby: ['Vaduthala', 'Kaloor', 'Marine Drive'],
    related: [
      { name: 'Vaduthala', href: '/cleaning-service-vaduthala' },
      { name: 'Kaloor', href: '/cleaning-service-kaloor' },
      { name: 'Marine Drive', href: '/cleaning-service-marine-drive' },
    ],
    reviews: [
      {
        name: 'Celine J.',
        location: 'Pachalam',
        text: 'Independent house. Outdoor sit-out and interiors in one visit.',
      },
      {
        name: 'Vineeth S.',
        location: 'Chittoor Road',
        text: 'Booked from office. Crew coordinated with our gate.',
      },
      { name: 'Mini A.', location: 'Pachalam', text: 'Sofa + home clean. Fair price shown before confirm.' },
    ],
  },
  {
    key: 'vaduthala',
    name: 'Vaduthala',
    slug: 'cleaning-service-vaduthala',
    eyebrow: 'Vaduthala, Chittoor & Varapuzha approach',
    blurb:
      'Home cleaning in Vaduthala for river-side houses and new flats toward Chittoor and Varapuzha. Humidity in bathrooms is the usual brief — we treat grout, not just mop.',
    highlightIcon: 'Waves',
    highlightText: 'Chittoor river belt',
    areas: ['Vaduthala Town', 'Chittoor', 'Varapuzha approach', 'Container Road edge', 'Pachalam north'],
    landmarks: ['Vaduthala Church', 'Chittoor', 'Varapuzha bridge approach'],
    nearby: ['Pachalam', 'Cheranalloor', 'Eloor'],
    related: [
      { name: 'Pachalam', href: '/cleaning-service-pachalam' },
      { name: 'Cheranalloor', href: '/cleaning-service-cheranalloor' },
      { name: 'Eloor', href: '/cleaning-service-eloor' },
    ],
    reviews: [
      {
        name: 'George K.',
        location: 'Vaduthala',
        text: 'River-side bathrooms. Anti-fungal treatment lasted through last monsoon.',
      },
      {
        name: 'Raji N.',
        location: 'Chittoor',
        text: 'New apartment move-in. Kitchen cabinets were dusty from packing.',
      },
      {
        name: 'Tony P.',
        location: 'Vaduthala',
        text: 'Parents’ house monthly clean. Reliable WhatsApp updates.',
      },
    ],
  },
  {
    key: 'mamangalam',
    name: 'Mamangalam',
    slug: 'cleaning-service-mamangalam',
    eyebrow: 'Mamangalam, Palarivattom & Vennala',
    blurb:
      'Apartment cleaning in Mamangalam between Palarivattom Signal and Vennala Bypass. Traffic dust on balconies and weekday slots for IT commuters.',
    highlightIcon: 'Bus',
    highlightText: 'Palarivattom–Vennala',
    areas: [
      'Mamangalam Junction',
      'Palarivattom east',
      'Vennala west',
      'Pipeline Road edge',
      'Changampuzha Nagar',
    ],
    landmarks: ['Mamangalam Junction', 'Changampuzha Nagar', 'Palarivattom Bypass'],
    nearby: ['Palarivattom', 'Vennala', 'Edappally'],
    related: [
      { name: 'Palarivattom', href: '/cleaning-service-palarivattom' },
      { name: 'Vennala', href: '/cleaning-service-vennala' },
      { name: 'Edappally', href: '/cleaning-service-edappally' },
    ],
    reviews: [
      {
        name: 'Divya K.',
        location: 'Mamangalam',
        text: '2BHK balcony wash plus interiors. Dust from the bypass is the issue here.',
      },
      {
        name: 'Ajay R.',
        location: 'Mamangalam',
        text: 'Same-day after a 10 AM booking. Crew knew the apartment gate process.',
      },
      {
        name: 'Fathima N.',
        location: 'Changampuzha Nagar',
        text: 'Kitchen degrease was thorough. Recurring plan next.',
      },
    ],
  },
  {
    key: 'ponekkara',
    name: 'Ponekkara',
    slug: 'cleaning-service-ponekkara',
    eyebrow: 'Ponekkara, Edappally Metro & Lulu corridor',
    blurb:
      'Family-house and high-rise cleaning in Ponekkara. Edappally Metro commuters and Lulu-side apartments that need weekday deep cleans while they are at work.',
    highlightIcon: 'Train',
    highlightText: 'Metro & Lulu side',
    areas: ['Ponekkara Road', 'Edappally south', 'Lulu corridor', 'Elamakkara edge', 'NH Bypass Ponekkara'],
    landmarks: ['Ponekkara', 'Edappally Metro approach', 'Lulu Mall approach'],
    nearby: ['Edappally', 'Elamakkara', 'Kalamassery'],
    related: [
      { name: 'Edappally', href: '/cleaning-service-edappally' },
      { name: 'Elamakkara', href: '/cleaning-service-elamakkara' },
      { name: 'Kalamassery', href: '/cleaning-service-kalamassery' },
    ],
    reviews: [
      {
        name: 'Nisha T.',
        location: 'Ponekkara',
        text: '3BHK while we were at Lulu. Locked up and sent photos.',
      },
      {
        name: 'Rahul S.',
        location: 'Ponekkara',
        text: 'Post-renovation. Tile haze and paint dust fully removed.',
      },
      { name: 'Ancy M.', location: 'Edappally south', text: 'Bathroom anti-fungal before school reopened.' },
    ],
  },
  {
    key: 'ernakulam-south',
    name: 'Ernakulam South',
    slug: 'cleaning-service-ernakulam-south',
    eyebrow: 'Ernakulam Junction, Pallimukku & Ravipuram',
    blurb:
      'Central-city cleaning around Ernakulam South / Junction. Pallimukku offices, Ravipuram apartments and MG Road-south homes that need early slots before traffic builds.',
    highlightIcon: 'Train',
    highlightText: 'Junction & Pallimukku',
    areas: ['Ernakulam Junction', 'Pallimukku', 'Ravipuram', 'MG Road south', 'Valanjambalam'],
    landmarks: ['Ernakulam Junction', 'Pallimukku Junction', 'Ravipuram'],
    nearby: ['Ravipuram', 'Kadavanthra', 'M.G. Road'],
    related: [
      { name: 'Ravipuram', href: '/cleaning-service-ravipuram' },
      { name: 'Kadavanthra', href: '/cleaning-service-kadavanthra' },
      { name: 'M.G. Road', href: '/cleaning-service-mg-road' },
    ],
    reviews: [
      {
        name: 'Priya H.',
        location: 'Pallimukku',
        text: 'Office pantry and restrooms on a Saturday morning. Punctual.',
      },
      { name: 'Sajan V.', location: 'Junction', text: 'Studio apartment near the station. Fast and tidy.' },
      {
        name: 'Meera L.',
        location: 'Ernakulam South',
        text: 'Booked online in under a minute. Crew found parking.',
      },
    ],
  },
  {
    key: 'ravipuram',
    name: 'Ravipuram',
    slug: 'cleaning-service-ravipuram',
    eyebrow: 'Ravipuram, Hospital Road & MG Road south',
    blurb:
      'Apartment and clinic-adjacent home cleaning in Ravipuram. Hospital-road dust and compact city flats — a crew used to tight parking and society rules.',
    highlightIcon: 'HeartHandshake',
    highlightText: 'Hospital Road flats',
    areas: ['Ravipuram Junction', 'Hospital Road', 'MG Road south', 'Pallimukku edge', 'Thevara north'],
    landmarks: ['Ravipuram Junction', 'Hospital Road', 'MG Road south'],
    nearby: ['Ernakulam South', 'Kadavanthra', 'Thevara'],
    related: [
      { name: 'Ernakulam South', href: '/cleaning-service-ernakulam-south' },
      { name: 'Kadavanthra', href: '/cleaning-service-kadavanthra' },
      { name: 'Thevara', href: '/cleaning-service-thevara' },
    ],
    reviews: [
      {
        name: 'Dr. Anil P.',
        location: 'Hospital Road',
        text: 'Clinic + attached home. Disinfectant pass on floors as requested.',
      },
      {
        name: 'Shalini R.',
        location: 'Ravipuram',
        text: '2BHK deep clean. Kitchen cabinets were the highlight.',
      },
      {
        name: 'Jose C.',
        location: 'Ravipuram',
        text: 'Recurring fortnightly. No missed slots in three months.',
      },
    ],
  },
  {
    key: 'padamugal',
    name: 'Padamugal',
    slug: 'cleaning-service-padamugal',
    eyebrow: 'Padamugal, Infopark corridor & Thrikkakara',
    blurb:
      'IT-corridor apartment cleaning in Padamugal. Construction dust from Smart City and Infopark-side towers — weekday deep cleans timed to office hours.',
    highlightIcon: 'Building',
    highlightText: 'Infopark-side towers',
    areas: ['Padamugal Junction', 'Infopark residential', 'Thrikkakara', 'Vazhakkala east', 'CSEZ approach'],
    landmarks: ['Padamugal Junction', 'Infopark approach', 'CSEZ approach'],
    nearby: ['Vazhakkala', 'Kakkanad', 'Thrikkakara'],
    related: [
      { name: 'Vazhakkala', href: '/cleaning-service-vazhakkala' },
      { name: 'Kakkanad', href: '/cleaning-service-kakkanad' },
      { name: 'Thrikkakara', href: '/cleaning-service-thrikkakara' },
    ],
    reviews: [
      {
        name: 'Vivek M.',
        location: 'Padamugal',
        text: 'High-rise window tracks full of Infopark dust. They got into the channels.',
      },
      {
        name: 'Anjali S.',
        location: 'Padamugal',
        text: 'Booked before noon, same-day 3BHK. Supervisor photos at handover.',
      },
      {
        name: 'Rohit K.',
        location: 'Thrikkakara edge',
        text: 'Monthly plan for a rented flat. Consistent quality.',
      },
    ],
  },
  {
    key: 'kumbalam',
    name: 'Kumbalam',
    slug: 'cleaning-service-kumbalam',
    eyebrow: 'Kumbalam, Aroor & Backwater Homes',
    blurb:
      'Villa and island-edge home cleaning in Kumbalam. Backwater humidity, weekend houses and Aroor-road family homes — deep cleaning built for this water belt.',
    highlightIcon: 'Waves',
    highlightText: 'Backwater villas',
    areas: ['Kumbalam Town', 'Aroor approach', 'Kundanoor south', 'Palluruthy east', 'Nettoor west'],
    landmarks: ['Kumbalam Bridge', 'Aroor Road', 'Backwater homes'],
    nearby: ['Aroor', 'Palluruthy', 'Kumbalangi'],
    related: [
      { name: 'Aroor', href: '/cleaning-service-aroor' },
      { name: 'Palluruthy', href: '/cleaning-service-palluruthy' },
      { name: 'Kumbalangi', href: '/cleaning-service-kumbalangi' },
    ],
    reviews: [
      {
        name: 'Thomas V.',
        location: 'Kumbalam',
        text: 'Weekend villa. Bathrooms and sit-out after a month of guests.',
      },
      {
        name: 'Beena R.',
        location: 'Kumbalam',
        text: 'Kitchen chimney plus home. Coconut-oil film came off.',
      },
      {
        name: 'Naveen P.',
        location: 'Aroor approach',
        text: 'Booked two days ahead. Crew found the lane without a call.',
      },
    ],
  },
  {
    key: 'nedumbassery',
    name: 'Nedumbassery',
    slug: 'cleaning-service-nedumbassery',
    eyebrow: 'CIAL, Airport Road & Athani',
    blurb:
      'Airport-corridor home and villa cleaning in Nedumbassery. CIAL staff quarters, Athani houses and transit flats that need a crew which can work around flight rosters.',
    highlightIcon: 'Briefcase',
    highlightText: 'CIAL airport corridor',
    areas: ['Nedumbassery Town', 'CIAL surroundings', 'Athani', 'Airport Road', 'Angamaly south'],
    landmarks: ['Cochin International Airport', 'Athani Junction', 'Airport Road'],
    nearby: ['Athani', 'Angamaly', 'Aluva'],
    related: [
      { name: 'Athani', href: '/cleaning-service-athani' },
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
      { name: 'Aluva', href: '/cleaning-service-aluva' },
    ],
    reviews: [
      {
        name: 'Capt. Nair',
        location: 'Nedumbassery',
        text: 'Booked around a layover. Flat ready when we landed.',
      },
      { name: 'Liya T.', location: 'Airport Road', text: 'Villa deep clean before relatives from the Gulf.' },
      {
        name: 'Manoj E.',
        location: 'Nedumbassery',
        text: 'Same-day kitchen after a noon booking. Fair online price.',
      },
    ],
  },
  {
    key: 'athani',
    name: 'Athani',
    slug: 'cleaning-service-athani',
    eyebrow: 'Athani Junction, Airport Highway & Chengamanad',
    blurb:
      'Home cleaning in Athani on the airport highway. Chengamanad-side houses and NH-544 dust — weekday deep cleans between Aluva and CIAL.',
    highlightIcon: 'Bus',
    highlightText: 'Airport highway',
    areas: ['Athani Junction', 'Chengamanad', 'Airport Highway', 'Nedumbassery west', 'Aluva north'],
    landmarks: ['Athani Junction', 'Airport Highway', 'Chengamanad'],
    nearby: ['Nedumbassery', 'Aluva', 'Angamaly'],
    related: [
      { name: 'Nedumbassery', href: '/cleaning-service-nedumbassery' },
      { name: 'Aluva', href: '/cleaning-service-aluva' },
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
    ],
    reviews: [
      {
        name: 'Sreeja P.',
        location: 'Athani',
        text: 'Highway dust on balcony rails. They washed, not just dusted.',
      },
      { name: 'Basil K.', location: 'Chengamanad', text: 'Independent house including sit-out. On time.' },
      {
        name: 'Remya S.',
        location: 'Athani',
        text: 'Bathroom grout before school term. Visible difference.',
      },
    ],
  },
  {
    key: 'kalady',
    name: 'Kalady',
    slug: 'cleaning-service-kalady',
    eyebrow: 'Kalady Town, Adi Sankara & River Road',
    blurb:
      'Town and riverside home cleaning in Kalady. Family houses, ashram-side stays and Angamaly–Perumbavoor corridor homes within the 50 km Kochi dispatch.',
    highlightIcon: 'Sun',
    highlightText: 'River-town homes',
    areas: ['Kalady Town', 'Adi Sankara area', 'River Road', 'Angamaly east', 'Malayattoor approach'],
    landmarks: ['Adi Sankara Janmabhoomi', 'Kalady Town', 'Periyar river road'],
    nearby: ['Angamaly', 'Malayattoor', 'Perumbavoor'],
    related: [
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
      { name: 'Malayattoor', href: '/cleaning-service-malayattoor' },
      { name: 'Perumbavoor', href: '/cleaning-service-perumbavoor' },
    ],
    reviews: [
      {
        name: 'Unni M.',
        location: 'Kalady',
        text: 'Town house before a function. Hall and kitchen done the same day.',
      },
      {
        name: 'Sr. Tessy',
        location: 'Kalady',
        text: 'Respectful crew in a traditional home. Bathrooms thoroughly cleaned.',
      },
      {
        name: 'Vishnu R.',
        location: 'River Road',
        text: 'Booked online. No locality surcharge on the form.',
      },
    ],
  },
  {
    key: 'kolenchery',
    name: 'Kolenchery',
    slug: 'cleaning-service-kolenchery',
    eyebrow: 'Kolenchery, MOSC & Puthencruz Road',
    blurb:
      'Home and staff-quarter cleaning in Kolenchery. MOSC Medical College families, town houses and Puthencruz-road villas on the eastern Ernakulam route.',
    highlightIcon: 'HeartHandshake',
    highlightText: 'MOSC & town',
    areas: ['Kolenchery Town', 'MOSC area', 'Puthencruz Road', 'Muvattupuzha approach', 'Pattimattom edge'],
    landmarks: ['MOSC Medical College', 'Kolenchery Town', 'Puthencruz Road'],
    nearby: ['Puthencruz', 'Muvattupuzha', 'Kizhakambalam'],
    related: [
      { name: 'Puthencruz', href: '/cleaning-service-puthencruz' },
      { name: 'Muvattupuzha', href: '/cleaning-service-muvattupuzha' },
      { name: 'Kizhakambalam', href: '/cleaning-service-kizhakambalam' },
    ],
    reviews: [
      {
        name: 'Dr. Leena',
        location: 'MOSC area',
        text: 'Booked around duty hours. 3BHK ready when I got back.',
      },
      {
        name: 'Biju A.',
        location: 'Kolenchery',
        text: 'Villa floor machine-scrub. Better than mop-only cleaners.',
      },
      {
        name: 'Anna G.',
        location: 'Puthencruz Road',
        text: 'Kitchen + bathrooms. Honest pricing on the form.',
      },
    ],
  },
  {
    key: 'kothamangalam',
    name: 'Kothamangalam',
    slug: 'cleaning-service-kothamangalam',
    eyebrow: 'Kothamangalam Town, College Road & Muvattupuzha',
    blurb:
      'Town-home cleaning in Kothamangalam, about 45 km from Kochi. College-road houses, market-side homes and Muvattupuzha-road villas on the same Greater Kochi rate card.',
    highlightIcon: 'Users',
    highlightText: 'Eastern town homes',
    areas: ['Kothamangalam Town', 'College Road', 'Muvattupuzha Road', 'Thattekkad approach', 'Market area'],
    landmarks: ['Kothamangalam Town', 'College Road', 'Muvattupuzha Road'],
    nearby: ['Muvattupuzha', 'Kolenchery', 'Perumbavoor'],
    related: [
      { name: 'Muvattupuzha', href: '/cleaning-service-muvattupuzha' },
      { name: 'Kolenchery', href: '/cleaning-service-kolenchery' },
      { name: 'Perumbavoor', href: '/cleaning-service-perumbavoor' },
    ],
    reviews: [
      {
        name: 'Santhosh P.',
        location: 'Kothamangalam',
        text: 'Town house before Onam. Team travelled from the Kochi side on time.',
      },
      {
        name: 'Linta J.',
        location: 'College Road',
        text: 'Student-rental turnover clean. Fast and complete.',
      },
      { name: 'Eldho K.', location: 'Kothamangalam', text: 'Bathroom anti-fungal. Grout looks reset.' },
    ],
  },
  {
    key: 'chottanikkara',
    name: 'Chottanikkara',
    slug: 'cleaning-service-chottanikkara',
    eyebrow: 'Chottanikkara Temple Town & Thripunithura east',
    blurb:
      'Temple-town and villa cleaning in Chottanikkara. Festival-week turnarounds, family houses and Thripunithura-east homes that need a crew used to pilgrim-season dust.',
    highlightIcon: 'Sun',
    highlightText: 'Temple-town homes',
    areas: ['Chottanikkara Town', 'Temple Road', 'Thripunithura east', 'Irumpanam south', 'Amballoor edge'],
    landmarks: ['Chottanikkara Temple', 'Temple Road', 'Thripunithura east'],
    nearby: ['Thripunithura', 'Irumpanam', 'Udayamperoor'],
    related: [
      { name: 'Thripunithura', href: '/cleaning-service-thripunithura' },
      { name: 'Irumpanam', href: '/cleaning-service-irumpanam' },
      { name: 'Udayamperoor', href: '/cleaning-service-udayamperoor' },
    ],
    reviews: [
      {
        name: 'Radha N.',
        location: 'Chottanikkara',
        text: 'House clean before a family pooja week. Respectful and thorough.',
      },
      {
        name: 'Vinod S.',
        location: 'Temple Road',
        text: 'Villa including courtyard. Same-day when we booked early.',
      },
      {
        name: 'Anitha P.',
        location: 'Chottanikkara',
        text: 'Kitchen degrease after festival cooking. Worth it.',
      },
    ],
  },
  {
    key: 'aroor',
    name: 'Aroor',
    slug: 'cleaning-service-aroor',
    eyebrow: 'Aroor, NH 66 & Industrial Belt',
    blurb:
      'Home and small-office cleaning in Aroor on NH 66. Industrial-belt dust, highway apartments and Kumbalam-side houses — dispatched from the same Kochi HQ.',
    highlightIcon: 'Briefcase',
    highlightText: 'NH 66 industrial belt',
    areas: ['Aroor Town', 'NH 66 Aroor', 'Eramalloor edge', 'Kumbalam south', 'Cherthala approach'],
    landmarks: ['Aroor Junction', 'NH 66', 'Industrial estate approach'],
    nearby: ['Kumbalam', 'Thoppumpady', 'Kumbalangi'],
    related: [
      { name: 'Kumbalam', href: '/cleaning-service-kumbalam' },
      { name: 'Thoppumpady', href: '/cleaning-service-thoppumpady' },
      { name: 'Kumbalangi', href: '/cleaning-service-kumbalangi' },
    ],
    reviews: [
      { name: 'Prasad T.', location: 'Aroor', text: 'Highway apartment. Balcony film from trucks is gone.' },
      { name: 'Shiny E.', location: 'Aroor', text: 'Home + small office the same week. Easy booking.' },
      { name: 'Noufal R.', location: 'NH 66', text: 'Kitchen and bathrooms. Price matched the form.' },
    ],
  },
  {
    key: 'varapuzha',
    name: 'Varapuzha',
    slug: 'cleaning-service-varapuzha',
    eyebrow: 'Varapuzha Bridge, Cheranalloor & Paravur Road',
    blurb:
      'Riverside home cleaning in Varapuzha. Bridge-side houses, new apartments and Paravur-road families — a north Kochi crew that already runs Cheranalloor and Eloor.',
    highlightIcon: 'Waves',
    highlightText: 'Bridge & river homes',
    areas: ['Varapuzha Town', 'Varapuzha Bridge', 'Cheranalloor north', 'Paravur Road', 'Eloor west'],
    landmarks: ['Varapuzha Bridge', 'Varapuzha Town', 'Paravur Road'],
    nearby: ['Cheranalloor', 'Eloor', 'North Paravur'],
    related: [
      { name: 'Cheranalloor', href: '/cleaning-service-cheranalloor' },
      { name: 'Eloor', href: '/cleaning-service-eloor' },
      { name: 'North Paravur', href: '/cleaning-service-north-paravur' },
    ],
    reviews: [
      {
        name: 'Jibin P.',
        location: 'Varapuzha',
        text: 'River humidity in two bathrooms. Treatment was the right call.',
      },
      {
        name: 'Sini A.',
        location: 'Bridge side',
        text: 'New flat move-in. Windows and kitchen first, as asked.',
      },
      {
        name: 'Martin J.',
        location: 'Varapuzha',
        text: 'Booked for parents. Crew called before reaching the lane.',
      },
    ],
  },
  {
    key: 'piravom',
    name: 'Piravom',
    slug: 'cleaning-service-piravom',
    eyebrow: 'Piravom Town, Mulanthuruthy Road & River',
    blurb:
      'Town-home cleaning in Piravom, about 32 km from Kochi. River-side houses, market homes and Mulanthuruthy-road villas on the Greater Kochi dispatch.',
    highlightIcon: 'MapPin',
    highlightText: 'River-town dispatch',
    areas: ['Piravom Town', 'Mulanthuruthy Road', 'River side', 'Elanji approach', 'Pampakuda edge'],
    landmarks: ['Piravom Town', 'Piravom Bridge', 'Mulanthuruthy Road'],
    nearby: ['Mulanthuruthy', 'Muvattupuzha', 'Kolenchery'],
    related: [
      { name: 'Mulanthuruthy', href: '/cleaning-service-mulanthuruthy' },
      { name: 'Muvattupuzha', href: '/cleaning-service-muvattupuzha' },
      { name: 'Kolenchery', href: '/cleaning-service-kolenchery' },
    ],
    reviews: [
      {
        name: 'Mathew K.',
        location: 'Piravom',
        text: 'Town house before Christmas. Team arrived from Kochi on the promised slot.',
      },
      { name: 'Jeena S.', location: 'River side', text: 'Villa bathrooms. Mould on grout is gone.' },
      { name: 'Arun B.', location: 'Piravom', text: 'Online price, no extra “out of town” fee at checkout.' },
    ],
  },
  {
    key: 'kizhakambalam',
    name: 'Kizhakambalam',
    slug: 'cleaning-service-kizhakambalam',
    eyebrow: 'Kizhakambalam, Kitex & Infopark east',
    blurb:
      'Township and villa cleaning in Kizhakambalam. Kitex-side homes, Infopark-east apartments and Puthencruz-road houses that pick up construction dust all week.',
    highlightIcon: 'Building',
    highlightText: 'Infopark east',
    areas: ['Kizhakambalam Town', 'Kitex area', 'Infopark east', 'Pattimattom', 'Puthencruz north'],
    landmarks: ['Kizhakambalam Town', 'Kitex', 'Infopark east'],
    nearby: ['Kakkanad', 'Puthencruz', 'Kolenchery'],
    related: [
      { name: 'Kakkanad', href: '/cleaning-service-kakkanad' },
      { name: 'Puthencruz', href: '/cleaning-service-puthencruz' },
      { name: 'Kolenchery', href: '/cleaning-service-kolenchery' },
    ],
    reviews: [
      {
        name: 'Nikhil A.',
        location: 'Kizhakambalam',
        text: 'Villa after construction next door. Dust in every track.',
      },
      { name: 'Reshma P.', location: 'Kitex area', text: 'Fortnightly 3BHK. Bathrooms stay usable.' },
      {
        name: 'Jithin S.',
        location: 'Kizhakambalam',
        text: 'Same-day when I booked at 9 AM. Confirmed instantly.',
      },
    ],
  },
  {
    key: 'puthencruz',
    name: 'Puthencruz',
    slug: 'cleaning-service-puthencruz',
    eyebrow: 'Puthencruz, SPA Road & Kolenchery',
    blurb:
      'Cleaning service in Puthencruz for Seaport-Airport Road villas and town houses toward Kolenchery. Construction dust on SPA Road is the usual brief.',
    highlightIcon: 'Bus',
    highlightText: 'SPA Road east',
    areas: ['Puthencruz Town', 'Seaport-Airport Road', 'Kolenchery west', 'Vadavucode', 'Irumpanam east'],
    landmarks: ['Puthencruz Town', 'Seaport-Airport Road', 'Kolenchery Road'],
    nearby: ['Irumpanam', 'Kolenchery', 'Kizhakambalam'],
    related: [
      { name: 'Irumpanam', href: '/cleaning-service-irumpanam' },
      { name: 'Kolenchery', href: '/cleaning-service-kolenchery' },
      { name: 'Kizhakambalam', href: '/cleaning-service-kizhakambalam' },
    ],
    reviews: [
      {
        name: 'Karthika V.',
        location: 'Puthencruz',
        text: 'New villa on SPA Road. Post-construction residue needed machines.',
      },
      {
        name: 'Saji T.',
        location: 'Puthencruz',
        text: 'Monthly clean. Garden sit-out and interiors both done.',
      },
      {
        name: 'Meenu R.',
        location: 'Vadavucode',
        text: 'Kitchen chimney plus home. Booking form was clear.',
      },
    ],
  },
  {
    key: 'udayamperoor',
    name: 'Udayamperoor',
    slug: 'cleaning-service-udayamperoor',
    eyebrow: 'Udayamperoor, Thripunithura south & Lake Road',
    blurb:
      'Villa and lake-side home cleaning in Udayamperoor. Thripunithura-south families and backwater-edge houses that need extra bathroom care in humidity.',
    highlightIcon: 'Waves',
    highlightText: 'Lake-side villas',
    areas: ['Udayamperoor Town', 'Thripunithura south', 'Lake Road', 'Kandanad edge', 'Chottanikkara west'],
    landmarks: ['Udayamperoor Town', 'Lake Road', 'Thripunithura south'],
    nearby: ['Thripunithura', 'Chottanikkara', 'Maradu'],
    related: [
      { name: 'Thripunithura', href: '/cleaning-service-thripunithura' },
      { name: 'Chottanikkara', href: '/cleaning-service-chottanikkara' },
      { name: 'Maradu', href: '/cleaning-service-maradu' },
    ],
    reviews: [
      {
        name: 'Paulose M.',
        location: 'Udayamperoor',
        text: 'Lake-side villa. Balconies and bathrooms were the priority.',
      },
      {
        name: 'Neethu K.',
        location: 'Lake Road',
        text: 'Deep clean before tenants moved in. Deposit photos look good.',
      },
      {
        name: 'Rajan E.',
        location: 'Udayamperoor',
        text: 'Booked two days out. Crew knew the lane off the main road.',
      },
    ],
  },
  {
    key: 'mulanthuruthy',
    name: 'Mulanthuruthy',
    slug: 'cleaning-service-mulanthuruthy',
    eyebrow: 'Mulanthuruthy, Piravom Road & Seminary',
    blurb:
      'Town and seminary-side home cleaning in Mulanthuruthy. Family houses toward Piravom and Thripunithura that sit inside the 50 km Kochi radius.',
    highlightIcon: 'Users',
    highlightText: 'Piravom Road homes',
    areas: [
      'Mulanthuruthy Town',
      'Piravom Road',
      'Seminary area',
      'Arakkunnam edge',
      'Thripunithura south-east',
    ],
    landmarks: ['Mulanthuruthy Town', 'Orthodox Seminary', 'Piravom Road'],
    nearby: ['Piravom', 'Thripunithura', 'Chottanikkara'],
    related: [
      { name: 'Piravom', href: '/cleaning-service-piravom' },
      { name: 'Thripunithura', href: '/cleaning-service-thripunithura' },
      { name: 'Chottanikkara', href: '/cleaning-service-chottanikkara' },
    ],
    reviews: [
      {
        name: 'Fr. John’s neighbour',
        location: 'Seminary area',
        text: 'Traditional home. Team was careful with furniture.',
      },
      {
        name: 'Sonia P.',
        location: 'Mulanthuruthy',
        text: 'Kitchen and three bathrooms. Same-day after a morning book.',
      },
      {
        name: 'Aby T.',
        location: 'Piravom Road',
        text: 'Villa floor scrub. Much better than the last local team.',
      },
    ],
  },
  {
    key: 'kodungallur',
    name: 'Kodungallur',
    slug: 'cleaning-service-kodungallur',
    eyebrow: 'Kodungallur Town, Temple Road & Coastal NH',
    blurb:
      'Coastal-town cleaning in Kodungallur, about 38 km from Kochi. Temple-road houses, NH homes and North Paravur-side families on the same rate card.',
    highlightIcon: 'Sun',
    highlightText: 'Coastal temple town',
    areas: ['Kodungallur Town', 'Temple Road', 'NH coastal', 'Methala', 'North Paravur north'],
    landmarks: ['Kodungallur Temple', 'Kodungallur Town', 'Coastal NH'],
    nearby: ['North Paravur', 'Cherai', 'Angamaly'],
    related: [
      { name: 'North Paravur', href: '/cleaning-service-north-paravur' },
      { name: 'Cherai', href: '/cleaning-service-cherai' },
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
    ],
    reviews: [
      {
        name: 'Sreedevi A.',
        location: 'Temple Road',
        text: 'House clean before a family gathering. Hall and kitchen first.',
      },
      { name: 'Nazar P.', location: 'Kodungallur', text: 'Shop-house plus home. Crew split the work well.' },
      {
        name: 'Gisha R.',
        location: 'Methala',
        text: 'Bathroom treatment. Salt-air mould is the issue here.',
      },
    ],
  },
  {
    key: 'cherai',
    name: 'Cherai',
    slug: 'cleaning-service-cherai',
    eyebrow: 'Cherai Beach, Vypin North & Homestays',
    blurb:
      'Beach-home and homestay cleaning in Cherai. Sand, salt and weekend turnarounds from Cherai Beach to Munambam — a Vypin crew that already works the island.',
    highlightIcon: 'Waves',
    highlightText: 'Beach & homestays',
    areas: ['Cherai Beach', 'Cherai Town', 'Munambam approach', 'Edavanakkad', 'North Paravur west'],
    landmarks: ['Cherai Beach', 'Cherai Town', 'Munambam'],
    nearby: ['Vypin', 'North Paravur', 'Kodungallur'],
    related: [
      { name: 'Vypin', href: '/cleaning-service-vypin' },
      { name: 'North Paravur', href: '/cleaning-service-north-paravur' },
      { name: 'Kodungallur', href: '/cleaning-service-kodungallur' },
    ],
    reviews: [
      {
        name: 'Lijo F.',
        location: 'Cherai Beach',
        text: 'Homestay turnaround between weekend groups. Floors and bathrooms ready.',
      },
      {
        name: 'Ammu S.',
        location: 'Cherai',
        text: 'Villa after a month of guests. Sand in every corner — they got it.',
      },
      {
        name: 'Peter D.',
        location: 'Edavanakkad',
        text: 'Booked from the Vypin page, crew already knew Cherai lanes.',
      },
    ],
  },
  {
    key: 'vaikom',
    name: 'Vaikom',
    slug: 'cleaning-service-vaikom',
    eyebrow: 'Vaikom Town, Lake Road & Kottayam border',
    blurb:
      'Town-home cleaning in Vaikom, about 40 km south of Kochi. Lake-road houses and temple-town homes on the Greater Kochi dispatch — same online rate card.',
    highlightIcon: 'Waves',
    highlightText: 'Lake-town homes',
    areas: ['Vaikom Town', 'Lake Road', 'Temple area', 'Thalayolaparambu edge', 'Vechoor approach'],
    landmarks: ['Vaikom Temple', 'Vaikom Town', 'Vembanad lake road'],
    nearby: ['Kottayam', 'Piravom', 'Kumbalangi'],
    related: [
      { name: 'Kottayam', href: '/cleaning-service-kottayam' },
      { name: 'Piravom', href: '/cleaning-service-piravom' },
      { name: 'Kumbalangi', href: '/cleaning-service-kumbalangi' },
    ],
    reviews: [
      {
        name: 'Gopalakrishnan N.',
        location: 'Vaikom',
        text: 'Town house before a wedding. Hall and two kitchens cleaned.',
      },
      {
        name: 'Bindu T.',
        location: 'Lake Road',
        text: 'Humidity in bathrooms. Treatment plus a full home clean.',
      },
      {
        name: 'Rakesh M.',
        location: 'Vaikom',
        text: 'Online booking. No surprise travel charge at payment.',
      },
    ],
  },
  {
    key: 'thiruvankulam',
    name: 'Thiruvankulam',
    slug: 'cleaning-service-thiruvankulam',
    eyebrow: 'Thiruvankulam, Hill Palace east & Irumpanam',
    blurb:
      'Villa and township cleaning in Thiruvankulam. Hill Palace-east homes, SPA Road access and Thripunithura families who want a local-feeling crew without a fake storefront.',
    highlightIcon: 'Building',
    highlightText: 'Hill Palace east',
    areas: [
      'Thiruvankulam Town',
      'Hill Palace east',
      'Irumpanam south',
      'Puthiyakavu',
      'Seaport-Airport Road',
    ],
    landmarks: ['Thiruvankulam Town', 'Hill Palace approach', 'Puthiyakavu'],
    nearby: ['Thripunithura', 'Irumpanam', 'Chottanikkara'],
    related: [
      { name: 'Thripunithura', href: '/cleaning-service-thripunithura' },
      { name: 'Irumpanam', href: '/cleaning-service-irumpanam' },
      { name: 'Chottanikkara', href: '/cleaning-service-chottanikkara' },
    ],
    reviews: [
      {
        name: 'Lakshmi R.',
        location: 'Thiruvankulam',
        text: 'Villa near Hill Palace. Garden sit-out and interiors both handled.',
      },
      {
        name: 'Vivek P.',
        location: 'Puthiyakavu',
        text: 'New flat. Post-handover dust in AC grills and tracks.',
      },
      {
        name: 'Anju S.',
        location: 'Thiruvankulam',
        text: 'Fortnightly plan. Supervisor check-in every visit.',
      },
    ],
  },
  {
    key: 'chellanam',
    name: 'Chellanam',
    slug: 'cleaning-service-chellanam',
    eyebrow: 'Chellanam, Kannamaly & Coastal West Kochi',
    blurb:
      'Coastal-home cleaning in Chellanam and Kannamaly. Salt air, fishing-village houses and weekend homes south of Palluruthy — a West Kochi crew that already runs this shore.',
    highlightIcon: 'Anchor',
    highlightText: 'Coastal West Kochi',
    areas: ['Chellanam Town', 'Kannamaly', 'Kandakkadavu', 'Palluruthy south', 'Kumbalangi west'],
    landmarks: ['Chellanam Church', 'Kannamaly', 'Coastal road'],
    nearby: ['Palluruthy', 'Kumbalangi', 'Fort Kochi'],
    related: [
      { name: 'Palluruthy', href: '/cleaning-service-palluruthy' },
      { name: 'Kumbalangi', href: '/cleaning-service-kumbalangi' },
      { name: 'Fort Kochi', href: '/cleaning-service-fort-kochi' },
    ],
    reviews: [
      {
        name: 'Shibu C.',
        location: 'Chellanam',
        text: 'House after a high-tide week. Floors and bathrooms needed a real reset.',
      },
      {
        name: 'Jancy P.',
        location: 'Kannamaly',
        text: 'Kitchen chimney plus home. Salt film on windows gone.',
      },
      { name: 'Raju D.', location: 'Chellanam', text: 'Booked for parents. Crew called at the junction.' },
    ],
  },
  {
    key: 'choornikkara',
    name: 'Choornikkara',
    slug: 'cleaning-service-choornikkara',
    eyebrow: 'Choornikkara, Aluva south & Kalamassery',
    blurb:
      'Home cleaning in Choornikkara between Aluva Metro and Kalamassery. Highway-side apartments and family houses on the NH 544 belt.',
    highlightIcon: 'Train',
    highlightText: 'Aluva–Kalamassery belt',
    areas: ['Choornikkara Town', 'Aluva south', 'Kalamassery north', 'NH 544', 'Chowara edge'],
    landmarks: ['Choornikkara Town', 'NH 544', 'Aluva Metro approach'],
    nearby: ['Aluva', 'Kalamassery', 'Edappally'],
    related: [
      { name: 'Aluva', href: '/cleaning-service-aluva' },
      { name: 'Kalamassery', href: '/cleaning-service-kalamassery' },
      { name: 'Edappally', href: '/cleaning-service-edappally' },
    ],
    reviews: [
      {
        name: 'Hema V.',
        location: 'Choornikkara',
        text: 'NH-side 2BHK. Balcony dust is constant — they washed the rails.',
      },
      {
        name: 'Aravind N.',
        location: 'Aluva south',
        text: 'Same-day after metro commute booking. Ready by evening.',
      },
      {
        name: 'Soumya K.',
        location: 'Choornikkara',
        text: 'Kitchen and bathrooms. Recurring plan started this month.',
      },
    ],
  },
  {
    key: 'malayattoor',
    name: 'Malayattoor',
    slug: 'cleaning-service-malayattoor',
    eyebrow: 'Malayattoor Church, Pilgrim Road & Kalady',
    blurb:
      'Pilgrim-town and hillside home cleaning in Malayattoor. Church-road houses, Kalady-side families and seasonal homestays that need a crew used to festival-week turnarounds.',
    highlightIcon: 'Sun',
    highlightText: 'Pilgrim-town homes',
    areas: ['Malayattoor Town', 'Church Road', 'Kalady east', 'Kodanad approach', 'Periyar hillside'],
    landmarks: ['Malayattoor Church', 'Pilgrim Road', 'Kalady east'],
    nearby: ['Kalady', 'Angamaly', 'Perumbavoor'],
    related: [
      { name: 'Kalady', href: '/cleaning-service-kalady' },
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
      { name: 'Perumbavoor', href: '/cleaning-service-perumbavoor' },
    ],
    reviews: [
      {
        name: 'Jose M.',
        location: 'Malayattoor',
        text: 'House clean before the feast week. Hall and kitchen ready the same day.',
      },
      {
        name: 'Lisy P.',
        location: 'Church Road',
        text: 'Homestay turnaround between pilgrim groups. Bathrooms and floors reset.',
      },
      {
        name: 'Saju K.',
        location: 'Malayattoor',
        text: 'Booked from the Kalady page. Crew already knew the hill road.',
      },
    ],
  },
  {
    key: 'cherthala',
    name: 'Cherthala',
    slug: 'cleaning-service-cherthala',
    eyebrow: 'Cherthala Town, NH 66 & Alappuzha north',
    blurb:
      'Town-home and highway-apartment cleaning in Cherthala, about 40 km south of Kochi. NH 66 dust, market-side houses and Alappuzha-north families on the same Greater Kochi rate card.',
    highlightIcon: 'Bus',
    highlightText: 'NH 66 & backwater town',
    areas: ['Cherthala Town', 'NH 66 Cherthala', 'Thuravoor south', 'Mararikulam north', 'Aroor south'],
    landmarks: ['Cherthala Town', 'NH 66', 'Railway station area'],
    nearby: ['Thuravoor', 'Mararikulam', 'Alappuzha'],
    related: [
      { name: 'Thuravoor', href: '/cleaning-service-thuravoor' },
      { name: 'Alappuzha', href: '/cleaning-service-alappuzha' },
      { name: 'Aroor', href: '/cleaning-service-aroor' },
    ],
    reviews: [
      {
        name: 'Sreekumar N.',
        location: 'Cherthala',
        text: 'Town house before a wedding. Hall and two bathrooms done in one visit.',
      },
      {
        name: 'Anju R.',
        location: 'NH 66',
        text: 'Highway flat. Balcony film from traffic was the main job.',
      },
      {
        name: 'Biju V.',
        location: 'Cherthala',
        text: 'Online price, no extra travel fee at checkout.',
      },
    ],
  },
  {
    key: 'thuravoor',
    name: 'Thuravoor',
    slug: 'cleaning-service-thuravoor',
    eyebrow: 'Thuravoor Temple, Aroor south & Cherthala',
    blurb:
      'Temple-town home cleaning in Thuravoor between Aroor and Cherthala. Festival-week houses, NH 66 apartments and family homes on the Alappuzha-north belt.',
    highlightIcon: 'Sun',
    highlightText: 'Temple town on NH 66',
    areas: ['Thuravoor Town', 'Temple Road', 'NH 66 Thuravoor', 'Arookutty edge', 'Cherthala north'],
    landmarks: ['Thuravoor Temple', 'NH 66', 'Arookutty approach'],
    nearby: ['Cherthala', 'Aroor', 'Kumbalam'],
    related: [
      { name: 'Cherthala', href: '/cleaning-service-cherthala' },
      { name: 'Aroor', href: '/cleaning-service-aroor' },
      { name: 'Kumbalam', href: '/cleaning-service-kumbalam' },
    ],
    reviews: [
      {
        name: 'Gopalakrishnan P.',
        location: 'Thuravoor',
        text: 'House clean before a family function. Respectful in a traditional home.',
      },
      {
        name: 'Nisha E.',
        location: 'Temple Road',
        text: 'Kitchen degrease after festival cooking. Worth it.',
      },
      {
        name: 'Rajan S.',
        location: 'Thuravoor',
        text: 'Booked two days out. Crew found the lane off the highway.',
      },
    ],
  },
  {
    key: 'mararikulam',
    name: 'Mararikulam',
    slug: 'cleaning-service-mararikulam',
    eyebrow: 'Marari Beach, Cherthala south & Homestays',
    blurb:
      'Beach-home and resort-adjacent cleaning in Mararikulam. Sand, salt and weekend villa turnarounds between Cherthala and Alappuzha — a coastal crew on the Kochi dispatch.',
    highlightIcon: 'Waves',
    highlightText: 'Marari beach homes',
    areas: ['Mararikulam Town', 'Marari Beach', 'Cherthala south', 'Arthunkal', 'Alappuzha north'],
    landmarks: ['Marari Beach', 'Mararikulam Temple', 'Arthunkal'],
    nearby: ['Cherthala', 'Alappuzha', 'Thuravoor'],
    related: [
      { name: 'Cherthala', href: '/cleaning-service-cherthala' },
      { name: 'Alappuzha', href: '/cleaning-service-alappuzha' },
      { name: 'Thuravoor', href: '/cleaning-service-thuravoor' },
    ],
    reviews: [
      {
        name: 'Thomas L.',
        location: 'Marari Beach',
        text: 'Villa turnaround between weekend guests. Sand in every corner — they got it.',
      },
      {
        name: 'Deepa M.',
        location: 'Mararikulam',
        text: 'Homestay bathrooms and kitchen. Same-day after a morning book.',
      },
      { name: 'Felix A.', location: 'Arthunkal', text: 'Family house. Salt film on windows is gone.' },
    ],
  },
  {
    key: 'chalakudy',
    name: 'Chalakudy',
    slug: 'cleaning-service-chalakudy',
    eyebrow: 'Chalakudy Town, NH 544 & Athirappilly Road',
    blurb:
      'Town-home cleaning in Chalakudy on the Kochi–Thrissur highway, about 40 km from the city. NH 544 apartments, market houses and Athirappilly-road villas on the same rate card.',
    highlightIcon: 'Bus',
    highlightText: 'Kochi–Thrissur highway',
    areas: ['Chalakudy Town', 'NH 544 Chalakudy', 'Athirappilly Road', 'Kodakara approach', 'Mala east'],
    landmarks: ['Chalakudy Town', 'NH 544', 'Athirappilly Road'],
    nearby: ['Angamaly', 'Mala', 'Thrissur'],
    related: [
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
      { name: 'Mala', href: '/cleaning-service-mala' },
      { name: 'Thrissur', href: '/cleaning-service-thrissur' },
    ],
    reviews: [
      {
        name: 'Vineeth P.',
        location: 'Chalakudy',
        text: 'Town house before Onam. Team travelled the NH and arrived on the slot.',
      },
      {
        name: 'Rini T.',
        location: 'Athirappilly Road',
        text: 'Villa including sit-out. Highway dust was the issue.',
      },
      {
        name: 'Jaison K.',
        location: 'Chalakudy',
        text: 'Kitchen and bathrooms. Price matched the booking form.',
      },
    ],
  },
  {
    key: 'mala',
    name: 'Mala',
    slug: 'cleaning-service-mala',
    eyebrow: 'Mala Town, Kodungallur Road & Chalakudy',
    blurb:
      'Town-home cleaning in Mala, about 40 km north of Kochi. Church-side houses, Kodungallur-road families and Chalakudy-west homes on the Greater Kochi dispatch.',
    highlightIcon: 'Users',
    highlightText: 'Mala town homes',
    areas: ['Mala Town', 'Kodungallur Road', 'Chalakudy west', 'Annamanada', 'Ashtamichira edge'],
    landmarks: ['Mala Town', 'Kodungallur Road', 'Annamanada'],
    nearby: ['Chalakudy', 'Kodungallur', 'Angamaly'],
    related: [
      { name: 'Chalakudy', href: '/cleaning-service-chalakudy' },
      { name: 'Kodungallur', href: '/cleaning-service-kodungallur' },
      { name: 'Angamaly', href: '/cleaning-service-angamaly' },
    ],
    reviews: [
      {
        name: 'Davis C.',
        location: 'Mala',
        text: 'Independent house. Outdoor sit-out and interiors in one visit.',
      },
      {
        name: 'Shiny J.',
        location: 'Annamanada',
        text: 'Bathroom anti-fungal before monsoon. Grout looks reset.',
      },
      { name: 'Bibin R.', location: 'Mala', text: 'Booked online. No locality surcharge.' },
    ],
  },
  {
    key: 'irinjalakuda',
    name: 'Irinjalakuda',
    slug: 'cleaning-service-irinjalakuda',
    eyebrow: 'Irinjalakuda Town, Koodalmanikyam & Thrissur south',
    blurb:
      'Temple-town cleaning in Irinjalakuda, about 50 km from Kochi on the Thrissur-south belt. Market homes, Koodalmanikyam-side houses and Chalakudy-west families.',
    highlightIcon: 'Sun',
    highlightText: 'Thrissur-south town',
    areas: ['Irinjalakuda Town', 'Koodalmanikyam area', 'Chalakudy west', 'Kattur', 'Thrissur south'],
    landmarks: ['Koodalmanikyam Temple', 'Irinjalakuda Town', 'Market Road'],
    nearby: ['Chalakudy', 'Thrissur', 'Mala'],
    related: [
      { name: 'Chalakudy', href: '/cleaning-service-chalakudy' },
      { name: 'Thrissur', href: '/cleaning-service-thrissur' },
      { name: 'Mala', href: '/cleaning-service-mala' },
    ],
    reviews: [
      {
        name: 'Santhosh N.',
        location: 'Irinjalakuda',
        text: 'Town house before a temple festival week. Hall and kitchen first.',
      },
      {
        name: 'Latha P.',
        location: 'Koodalmanikyam area',
        text: 'Respectful crew. Bathrooms thoroughly cleaned.',
      },
      {
        name: 'Arun M.',
        location: 'Irinjalakuda',
        text: 'Same Greater Kochi rate on the form. Confirmed instantly.',
      },
    ],
  },
];

/** Common search spellings that should canonicalize to the pages above. */
export const KOCHI_RADIUS_SPELLING_ALIASES: Record<string, string> = {
  'cleaning-service-vypeen': 'cleaning-service-vypin',
  'home-cleaning-vypeen': 'cleaning-service-vypin',
  'cleaning-service-mattanchery': 'cleaning-service-mattancherry',
  'home-cleaning-mattanchery': 'cleaning-service-mattancherry',
  'cleaning-service-cial': 'cleaning-service-nedumbassery',
  'cleaning-service-cochin-airport': 'cleaning-service-nedumbassery',
  'home-cleaning-cochin-airport': 'cleaning-service-nedumbassery',
  'cleaning-service-ernakulam-junction': 'cleaning-service-ernakulam-south',
  'cleaning-service-pallimukku': 'cleaning-service-ernakulam-south',
  'home-cleaning-pallimukku': 'cleaning-service-ernakulam-south',
  'cleaning-service-thoppumpadi': 'cleaning-service-thoppumpady',
  'home-cleaning-thoppumpadi': 'cleaning-service-thoppumpady',
  'cleaning-service-anagamaly': 'cleaning-service-angamaly',
  'home-cleaning-anagamaly': 'cleaning-service-angamaly',
  'cleaning-service-malayattor': 'cleaning-service-malayattoor',
  'home-cleaning-malayattor': 'cleaning-service-malayattoor',
  'cleaning-service-malayattoor-church': 'cleaning-service-malayattoor',
  'cleaning-service-alleppey': 'cleaning-service-alappuzha',
  'home-cleaning-alleppey': 'cleaning-service-alappuzha',
  'cleaning-service-allapuzha': 'cleaning-service-alappuzha',
  'cleaning-service-trichur': 'cleaning-service-thrissur',
  'home-cleaning-trichur': 'cleaning-service-thrissur',
};
